import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AgentMark } from '../ui/AgentMark';
import { ProcessingTag } from '../ui/ProcessingTag';
import { getAgent } from '../../utils/agents';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import type { AgentTask } from '../../types/db';
interface AgentLiveRowProps {
  task: AgentTask;
  paused: boolean;
  /** Changes when this agent should pulse once */
  pulseId?: number;
}
const toSec = (mmss: string) => {
  const [m, s] = mmss.split(':').map(Number);
  return m * 60 + s;
};
const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

/** One live agent thread: colour + icon, task, step, elapsed, model, and the last 3 events streaming in. */
export function AgentLiveRow({
  task,
  paused,
  pulseId
}: AgentLiveRowProps) {
  const a = getAgent(task.agent);
  const reduce = useMotionPreference();
  const [extra, setExtra] = useState(0);
  const running = task.status === 'bezig' && !paused;
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setExtra(e => e + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);
  const progress = task.step / task.totalSteps;
  const recent = task.events.slice(-3);
  return <div role="listitem" className="relative rounded-2xl px-3 py-3" data-id="element-530" data-mp="4h4nci19g4ej">
      {pulseId !== undefined && <motion.span key={pulseId} aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl" style={{
      boxShadow: `inset 0 0 0 1.5px ${a.color}`
    }} initial={{
      opacity: 1
    }} animate={{
      opacity: 0
    }} transition={{
      duration: 1.4,
      ease: 'easeOut'
    }} data-id="element-531" data-mp="j5irqxebs0x7" />}
      <div className="flex items-start gap-3" data-id="element-532" data-mp="2tyva64s3murg">
        <AgentMark agentId={task.agent} data-id="element-533" data-mp="2tzn5rflp4shq" />
        <div className="min-w-0 flex-1" data-id="element-534" data-mp="2p0k2o0pa0wce">
          <div className="flex items-baseline justify-between gap-3" data-id="element-535" data-mp="2p1v2jpflpjy8">
            <p className="truncate text-[15px] font-medium text-ink" data-id="element-536" data-mp="sxyuc53jzahz">
              <span style={{
              color: a.color
            }} data-id="element-537" data-mp="xw8r2k4xej5p">{a.name}</span> · {task.title}
            </p>
            <span className="shrink-0 text-[13px] tabular-nums text-ink-muted" data-id="element-538" data-mp="xy38hogznt51">
              stap {task.step}/{task.totalSteps}
            </span>
          </div>
          <p className={`truncate text-[14px] ${task.status === 'wacht' ? 'text-amber' : 'text-ink-soft'}`} data-id="element-539" data-mp="16yhyv28t7yfe">{task.stepLabel}</p>

          <div className="relative mt-2 h-[3px] overflow-hidden rounded-full" style={{
          backgroundColor: `${a.color}26`
        }} data-id="element-540" data-mp="1bu70db4e3pec">
            <motion.div className="absolute inset-y-0 left-0 rounded-full" style={{
            backgroundColor: a.color
          }} initial={false} animate={{
            width: `${progress * 100}%`
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-541" data-mp="1lm2xaiuqp9gz" />
            {running && !reduce && <motion.span aria-hidden className="absolute inset-y-0 w-6 rounded-full bg-white/50" initial={{
            left: '-10%'
          }} animate={{
            left: `${progress * 100}%`
          }} transition={{
            duration: 2.4 / Math.max(0.1, task.pace),
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-542" data-mp="1zm5hibqiseze" />}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-muted" data-id="element-543" data-mp="3656rvpq52d75">
            <span className="tabular-nums" data-id="element-544" data-mp="6o8iqqal8huz">{fmt(toSec(task.elapsed) + extra)}</span>
            <span data-id="element-545" data-mp="2o7uyf1lmub9">{task.model}</span>
            <ProcessingTag where={task.where} data-id="element-546" data-mp="2p9gxek5l17r" />
          </div>

          <ul className="mt-2 space-y-0.5 border-l border-white/10 pl-3 font-mono text-[13px]" aria-label={`Laatste gebeurtenissen ${a.name}`} data-id="element-547" data-mp="3u0ssewo028t7">
            <AnimatePresence initial={false} data-id="element-548" data-mp="3p0wvfhdpzhn5">
              {recent.map((e, i) => <motion.li key={`${task.events.length - recent.length + i}-${e}`} layout={!reduce} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.12
              }
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className={i === recent.length - 1 ? 'text-ink-soft' : 'text-ink-muted'} data-id="element-549" data-mp="qeu0nzo4s2i3" data-mpi={i}>
                  {e}
                </motion.li>)}
            </AnimatePresence>
          </ul>
        </div>
      </div>
    </div>;
}